import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { isDueBeforeReceipt, periodicityLabels, priorityLabels, sourceLabels } from '../../domain/taskRules'
import { errorMessage } from '../../lib/errors'
import { nowInBrasilia } from '../../lib/time'
import type { TaskFormValues } from '../../types/domain'
import { listCatalog } from '../catalogs/catalog.repository'
import { parseTaskForm, taskFormSchema } from './task.schema'
import { useSaveTask, useTask } from './task.queries'

type FieldErrors = Partial<Record<keyof TaskFormValues, string>>

function emptyValues(): TaskFormValues {
  const now = nowInBrasilia()
  return {
    title: '',
    description: '',
    clientId: '',
    assigneeId: '',
    departmentId: '',
    source: '',
    priority: 'normal',
    periodicity: 'none',
    receivedDate: now.date,
    receivedTime: now.time,
    dueDate: '',
    dueTime: '',
  }
}

export function TaskForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const editingTask = useTask(id)
  const clients = useQuery({ queryKey: ['catalog', 'clients'], queryFn: () => listCatalog('clients') })
  const assignees = useQuery({ queryKey: ['catalog', 'assignees'], queryFn: () => listCatalog('assignees') })
  const departments = useQuery({ queryKey: ['catalog', 'departments'], queryFn: () => listCatalog('departments') })
  const saveTask = useSaveTask()
  const [operationId] = useState(() => id ?? crypto.randomUUID())
  const [values, setValues] = useState<TaskFormValues>(emptyValues)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [hydratedId, setHydratedId] = useState<string | null>(null)

  useEffect(() => {
    const task = editingTask.data
    if (!task || task.id === hydratedId) return
    setValues({
      title: task.title,
      description: task.description ?? '',
      clientId: task.client_id,
      assigneeId: task.assignee_id,
      departmentId: task.department_id,
      source: task.source,
      priority: task.priority,
      periodicity: task.periodicity,
      receivedDate: task.received_date,
      receivedTime: task.received_time.slice(0, 5),
      dueDate: task.due_date,
      dueTime: task.due_time?.slice(0, 5) ?? '',
    })
    setHydratedId(task.id)
  }, [editingTask.data, hydratedId])

  const warning = useMemo(() => isDueBeforeReceipt(values), [values])
  const update = (field: keyof TaskFormValues, value: string) => {
    setValues((current) => ({ ...current, [field]: value }))
    setFieldErrors((current) => ({ ...current, [field]: undefined }))
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const result = taskFormSchema.safeParse(values)
    if (!result.success) {
      const nextErrors: FieldErrors = {}
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof TaskFormValues
        if (!nextErrors[field]) nextErrors[field] = issue.message
      }
      setFieldErrors(nextErrors)
      return
    }
    try {
      const task = await saveTask.mutateAsync(parseTaskForm(operationId, values))
      navigate(`/tarefas/${task.id}`, {
        replace: true,
        state: { message: id ? 'Tarefa atualizada com sucesso.' : 'Tarefa salva com sucesso.' },
      })
    } catch {
      // The mutation state renders the sanitized error and keeps this operation ID for a safe retry.
    }
  }

  if (id && editingTask.isLoading) return <p>Carregando tarefa…</p>
  if (id && !editingTask.isLoading && !editingTask.data) {
    return <p className="notice-error">Tarefa não encontrada.</p>
  }

  const visibleClients = clients.data?.filter((item) => item.is_active || item.id === values.clientId) ?? []
  const visibleAssignees = assignees.data?.filter((item) => item.is_active || item.id === values.assigneeId) ?? []
  const visibleDepartments = departments.data?.filter((item) => item.is_active || item.id === values.departmentId) ?? []

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm font-bold uppercase tracking-wider text-blue-700">{id ? 'Edição' : 'Registro rápido'}</p>
          <h1 className="text-3xl font-extrabold text-slate-950">{id ? 'Editar tarefa' : 'Nova tarefa'}</h1>
          <p className="mt-1 text-slate-600">Campos com * são obrigatórios.</p>
        </div>
        <Link className="font-bold text-blue-700 hover:underline" to={id ? `/tarefas/${id}` : '/'}>Cancelar</Link>
      </div>

      <form className="card grid gap-6 p-5 sm:p-7" onSubmit={submit} noValidate>
        <div className="grid gap-5 lg:grid-cols-2">
          <label className="field lg:col-span-2">
            <span>Título *</span>
            <input className="control" autoFocus maxLength={180} value={values.title} onChange={(event) => update('title', event.target.value)} />
            {fieldErrors.title ? <small className="text-red-700">{fieldErrors.title}</small> : null}
          </label>
          <label className="field lg:col-span-2">
            <span>Descrição</span>
            <textarea className="control min-h-28 resize-y" maxLength={5000} value={values.description} onChange={(event) => update('description', event.target.value)} />
          </label>
          <label className="field">
            <span>Cliente *</span>
            <select className="control" value={values.clientId} onChange={(event) => update('clientId', event.target.value)}>
              <option value="">Selecione</option>
              {visibleClients.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
            {fieldErrors.clientId ? <small className="text-red-700">{fieldErrors.clientId}</small> : null}
          </label>
          <label className="field">
            <span>Responsável *</span>
            <select className="control" value={values.assigneeId} onChange={(event) => update('assigneeId', event.target.value)}>
              <option value="">Selecione</option>
              {visibleAssignees.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
            {fieldErrors.assigneeId ? <small className="text-red-700">{fieldErrors.assigneeId}</small> : null}
          </label>
          <label className="field">
            <span>Departamento *</span>
            <select className="control" value={values.departmentId} onChange={(event) => update('departmentId', event.target.value)}>
              <option value="">Selecione</option>
              {visibleDepartments.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
            {fieldErrors.departmentId ? <small className="text-red-700">{fieldErrors.departmentId}</small> : null}
          </label>
          <label className="field">
            <span>Origem *</span>
            <select className="control" value={values.source} onChange={(event) => update('source', event.target.value)}>
              <option value="">Selecione</option>
              {Object.entries(sourceLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            {fieldErrors.source ? <small className="text-red-700">{fieldErrors.source}</small> : null}
          </label>
          <label className="field">
            <span>Prioridade *</span>
            <select className="control" value={values.priority} onChange={(event) => update('priority', event.target.value)}>
              <option value="">Selecione</option>
              {Object.entries(priorityLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            {fieldErrors.priority ? <small className="text-red-700">{fieldErrors.priority}</small> : null}
          </label>
          <label className="field">
            <span>Periodicidade</span>
            <select className="control" value={values.periodicity} onChange={(event) => update('periodicity', event.target.value)}>
              {Object.entries(periodicityLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
        </div>

        <fieldset className="grid gap-5 rounded-xl border border-slate-200 p-4 md:grid-cols-2">
          <legend className="px-2 font-extrabold text-slate-800">Recebimento</legend>
          <label className="field"><span>Data *</span><input className="control bg-slate-100" type="date" value={values.receivedDate} readOnly aria-readonly="true" />{fieldErrors.receivedDate ? <small className="text-red-700">{fieldErrors.receivedDate}</small> : null}</label>
          <label className="field"><span>Hora *</span><input className="control bg-slate-100" type="time" value={values.receivedTime} readOnly aria-readonly="true" />{fieldErrors.receivedTime ? <small className="text-red-700">{fieldErrors.receivedTime}</small> : null}</label>
          <small className="text-slate-500 md:col-span-2">Preenchimento automático; a data e a hora iniciais não podem ser alteradas.</small>
        </fieldset>

        <fieldset className="grid gap-5 rounded-xl border border-slate-200 p-4 md:grid-cols-2">
          <legend className="px-2 font-extrabold text-slate-800">Prazo</legend>
          <label className="field"><span>Data *</span><input className="control" type="date" value={values.dueDate} onChange={(event) => update('dueDate', event.target.value)} />{fieldErrors.dueDate ? <small className="text-red-700">{fieldErrors.dueDate}</small> : null}</label>
          <label className="field"><span>Hora (opcional)</span><input className="control" type="time" value={values.dueTime} onChange={(event) => update('dueTime', event.target.value)} /><small className="text-slate-500">Sem horário, vence às 23:59.</small></label>
        </fieldset>

        {warning ? <p className="notice-warning" role="alert">O prazo está antes do recebimento. Confira os dados; você ainda pode salvar.</p> : null}
        {saveTask.isError ? <p className="notice-error" role="alert">{errorMessage(saveTask.error)}</p> : null}
        {(clients.isError || assignees.isError || departments.isError) ? <p className="notice-error" role="alert">Não foi possível carregar clientes, responsáveis ou departamentos.</p> : null}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Link className="btn-secondary text-center" to={id ? `/tarefas/${id}` : '/'}>Cancelar</Link>
          <button className="btn-primary" disabled={saveTask.isPending || clients.isLoading || assignees.isLoading || departments.isLoading} type="submit">
            {saveTask.isPending ? 'Salvando…' : 'Salvar tarefa'}
          </button>
        </div>
      </form>
    </div>
  )
}
